import { Loader2 } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { type ConnectorOverview, useConnectorOverview, useMe } from "@/lib/api";
import { isAdminRole } from "@/lib/roles";

import { ConnectorUrl } from "./connector-url";
import { type DocsContent, useDocsContent } from "./content";
import { DocDetails, DocSubheading, RichText, Step, Steps } from "./doc-parts";
import { type ConnectPart, connectParts, everyoneFollowsOwner } from "./utils";

/**
 * The docs' first page, how to connect Claude: an Owner of the Claude
 * organization adds the connector once with the URL the overview gives, then
 * everyone connects with their own De Ceuster Microsoft account. Both let the
 * read-only tools run without asking each time. When the connector is off or
 * runs in local test mode, a note above the steps says so.
 *
 * The order follows the reader (`connectParts`). An admin, who sets the
 * connector up, reads the Owner's steps first, then everyone's. A colleague
 * reads their own steps first, ending with the test question, then who can
 * sign in, and finds the Owner's steps (with the URL) folded away at the end,
 * for the one who adds the connector.
 */
export function ConnectPage() {
  const { data: overview, isLoading } = useConnectorOverview();
  const { data: me } = useMe();
  const { connect: text } = useDocsContent();
  const parts = connectParts(isAdminRole(me?.role));
  const ownerSteps = <OwnerSteps overview={overview} isLoading={isLoading} text={text} />;

  const content: Record<ConnectPart, ReactNode> = {
    owner: (
      <>
        <DocSubheading>{text.ownerHeading}</DocSubheading>
        {ownerSteps}
      </>
    ),
    everyone: (
      <>
        <DocSubheading>
          {everyoneFollowsOwner(parts) ? text.everyoneHeading : text.everyoneFirstHeading}
        </DocSubheading>
        <Steps>
          {text.everyoneSteps.map((step, index) => (
            <Step key={index} number={index + 1}>
              <RichText text={step} />
            </Step>
          ))}
        </Steps>
      </>
    ),
    access: (
      <p className="text-muted">
        <RichText text={text.accessNote} />
      </p>
    ),
    ownerFolded: <DocDetails title={text.ownerFolded}>{ownerSteps}</DocDetails>,
  };

  return (
    <>
      <p>
        <RichText text={everyoneFollowsOwner(parts) ? text.intro : text.introEveryoneFirst} />
      </p>
      {overview && <ModeNote mode={overview.mode} text={text} />}
      {parts.map((part) => (
        <Fragment key={part}>{content[part]}</Fragment>
      ))}
    </>
  );
}

interface OwnerStepsProps {
  /** The overview, for the URL; undefined while it loads or when it failed. */
  overview: ConnectorOverview | undefined;
  isLoading: boolean;
  text: DocsContent["connect"];
}

/** The Owner's three steps, with the connector's URL after the second. */
function OwnerSteps({ overview, isLoading, text }: OwnerStepsProps) {
  const [openSettings, nameAndUrl, alwaysAllow] = text.ownerSteps;
  return (
    <Steps>
      <Step number={1}>
        <RichText text={openSettings} />
      </Step>
      <Step number={2}>
        <RichText text={nameAndUrl} />
        <UrlSlot overview={overview} isLoading={isLoading} text={text} />
      </Step>
      <Step number={3}>
        <RichText text={alwaysAllow} />
      </Step>
    </Steps>
  );
}

interface UrlSlotProps {
  /** The overview, for the URL; undefined while it loads or when it failed. */
  overview: ConnectorOverview | undefined;
  isLoading: boolean;
  text: DocsContent["connect"];
}

/** The URL to add, once the overview has it; otherwise why it is not there. */
function UrlSlot({ overview, isLoading, text }: UrlSlotProps) {
  if (isLoading) {
    return (
      <span className="text-muted mt-2 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {text.urlLoading}
      </span>
    );
  }
  if (overview?.url) {
    return <ConnectorUrl url={overview.url} />;
  }
  return (
    <span className="text-muted mt-1 block">{overview ? text.urlNotOnYet : text.urlFailed}</span>
  );
}

interface ModeNoteProps {
  mode: ConnectorOverview["mode"];
  text: DocsContent["connect"];
}

/** A note when the steps do not apply yet: the connector is off, or a local test. */
function ModeNote({ mode, text }: ModeNoteProps) {
  if (mode === "microsoft") {
    return null;
  }
  return (
    <p className="bg-accent-light rounded-md px-4 py-3">
      <RichText text={mode === "off" ? text.modeOff : text.modeLocal} />
    </p>
  );
}
